"use client";

import React, { useEffect, useMemo, useCallback, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import type { FUSportsLivescoreCompetitionGroup } from "fansunited-data-layer";
import { AdSlot } from "@/features/ads/AdSlot";
import { useLabels } from "@/lib/labels/LabelsProvider";
import type { LocaleCode } from "@/lib/labels/types";
import type { LivescoreSport, LivescoreFilter } from "@/views/livescore/utils/livescore";
import { toMatchOddsMap } from "@/features/odds/adapters";
import { useServerOddsByDate } from "@/features/odds/useServerOddsByDate";
import { primaryMarketsForSport } from "@/features/odds/sportMarkets";
import type { MatchOddsMap } from "@/features/odds/types";
import { useLivescoreState } from "@/views/livescore/state/LivescoreStateContext";
import { asCompetition } from "@/views/livescore/utils/livescore";
import { LivescoreSportSelector } from "../components/LivescoreSportSelector";
import { LivescoreFilters } from "../components/LivescoreFilters";
import { LivescoreDateNav } from "../components/LivescoreDateNav";
import { LivescoreMatchGroup } from "../components/LivescoreMatchGroup";
import { LivescoreLeaguePicker } from "../components/LivescoreLeaguePicker";
import { useFavouriteLeagues } from "../hooks/useFavouriteLeagues";
import { useLivescoreMatches } from "../hooks/useLivescoreMatches";
import { useGroupHydration } from "../hooks/useGroupHydration";
import type { LivescoreBoardSeed } from "@/features/live-data/livescoreParams";
import { useTvListingsByDate } from "../hooks/useTvListingsByDate";
import { isGroupHydrated, partitionGroups } from "../utils/grouping";
import { resolveBoardView } from "../utils/boardView";
import { MAX_COMPETITION_IDS } from "@/features/live-data/livescoreParams";
interface LivescoreClientProps {
  sports: LivescoreSport[];
  filters: LivescoreFilter[];
  defaultSport: LivescoreSport;
  defaultFilter: LivescoreFilter;
  popularCompetitionIds: string[];
  locale: LocaleCode;
  /**
   * Render mode. When `true`, the component drops its page-level chrome
   * (outer max-width + padding container, "Livescore" H1, top/bottom AdSlots)
   * so it can be composed inside a larger layout that owns those concerns
   * (e.g. the livescore-focused home variant). Defaults to `false` — the
   * dedicated `/livescore` page and the `LivescoreBlock` rich-text embed keep
   * their existing look.
   */
  embedded?: boolean;
  /** Server-rendered board for the initial sport / date; skips the first client fetch. */
  initialBoard?: LivescoreBoardSeed;
}
const SPORTS_WITH_DATE_NAV = new Set<LivescoreSport>(["football", "basketball", "tennis", "hockey", "american-football"]);
const SPORTS_WITH_MY_LEAGUES = new Set<LivescoreSport>(["football"]);
export function LivescoreClient({
  sports,
  filters,
  defaultSport,
  defaultFilter,
  popularCompetitionIds,
  locale,
  embedded = false,
  initialBoard
}: LivescoreClientProps) {
  const labels = useLabels();
  const ls = labels.livescore;
  const {
    sport,
    setSport,
    filter,
    setFilter,
    date,
    setDate,
    activeCompetitionId,
    setActiveCompetitionId,
    entities: sharedEntities,
    setEntities: setSharedEntities
  } = useLivescoreState({
    sport: defaultSport,
    filter: defaultFilter
  });
  // Collapsed-tail groups the user has opened this session; cleared when the board changes.
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const {
    favouriteIds: allFavouriteIds,
    save: saveFavourites,
    loaded: favouritesLoaded
  } = useFavouriteLeagues();
  // The same cap everywhere a favourite is fetched or expanded, so All and My leagues agree.
  const favouriteIds = useMemo(() => allFavouriteIds.slice(0, MAX_COMPETITION_IDS), [allFavouriteIds]);
  const initialFilterApplied = React.useRef(false);
  const activeFilters = filters.filter(f => {
    if (f === "my-leagues" && !SPORTS_WITH_MY_LEAGUES.has(sport)) return false;
    return true;
  });
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    setIsMobile(mql.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);
  const {
    popular,
    rest,
    entities: matchEntities,
    empty
  } = useLivescoreMatches({
    sport,
    filter,
    date,
    locale,
    favouriteIds,
    favouritesLoaded,
    initialBoard
  });
  const {
    hydrate,
    isSettled
  } = useGroupHydration({
    sport,
    filter,
    date,
    locale
  });
  // Parallel to the matches fetch: pull every broadcaster assignment for this date in one shot.
  // Returns a map keyed by native match ID → pre-resolved listings (modal data is ready on open,
  // no per-click lazy fetch), so the TV slot decision has data on first paint.
  const {
    tvByMatch
  } = useTvListingsByDate(date, locale);

  // Publish fetched entities into shared context so sibling components (e.g., a
  // parent-rendered TopLeaguesSidebar) and our own chip / MatchGroup renders
  // hydrate names + logos from a single merged map (server-hydrated seeds +
  // per-sport client fetches). Fallback branch in useLivescoreState makes this
  // a harmless no-op when LivescoreClient is rendered standalone.
  useEffect(() => {
    setSharedEntities(matchEntities);
  }, [matchEntities, setSharedEntities]);

  // Winner markets differ by sport (football: FT_1X2, basketball: WINNER_OT,
  // tennis: WINNER, ice hockey: WINNER_OT_PEN, …) — sportMarkets picks the
  // right code so the SDK's post-fetch market filter doesn't drop everything.
  const {
    oddsMap: rawOddsMap
  } = useServerOddsByDate({
    date,
    sport,
    markets: primaryMarketsForSport(sport),
    locale
  });
  const oddsMap = useMemo<MatchOddsMap>(() => toMatchOddsMap(rawOddsMap), [rawOddsMap]);

  // Switch to my-leagues on first load if the user has saved favourites.
  useEffect(() => {
    if (!favouritesLoaded || initialFilterApplied.current) return;
    initialFilterApplied.current = true;
    if (favouriteIds.length > 0 && activeFilters.includes("my-leagues")) {
      setFilter("my-leagues");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favouritesLoaded]);

  // When switching sport, reset filter if the current one isn't available for the new sport.
  useEffect(() => {
    if (!activeFilters.includes(filter)) setFilter("all");
  }, [sport]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setExpandedIds(new Set());
  }, [sport, filter, date]);

  // Stage 1 renders open; stage 2 renders as collapsed headers until opened. The League Picker's
  // single-competition chip narrows both sections to that competition and opens it.
  const {
    expanded,
    collapsed
  } = useMemo(() => {
    const parts = partitionGroups(popular.groups, rest.groups, filter, popularCompetitionIds, favouriteIds);
    if (!activeCompetitionId) return parts;
    const only = (g: FUSportsLivescoreCompetitionGroup) => g.competitionId === activeCompetitionId;
    return {
      expanded: [...parts.expanded.filter(only), ...parts.collapsed.filter(only)],
      collapsed: []
    };
  }, [popular.groups, rest.groups, filter, popularCompetitionIds, favouriteIds, activeCompetitionId]);

  // Every open group whose teams are not yet known gets them fetched once: favourites and the
  // active chip from the rest section, and anything the user opened in the collapsed tail.
  // `hydrate` attempts each competition once per scope, so this cannot loop.
  useEffect(() => {
    const open = [...expanded, ...collapsed.filter(g => expandedIds.has(g.competitionId))];
    for (const g of open) {
      if (!isGroupHydrated(g, sharedEntities)) hydrate(g.competitionId);
    }
  }, [expanded, collapsed, expandedIds, sharedEntities, hydrate]);
  const toggleExpanded = useCallback((competitionId: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(competitionId)) next.delete(competitionId);else next.add(competitionId);
      return next;
    });
  }, []);
  const groups = expanded;
  const showMyLeaguesEmptyState = filter === "my-leagues" && favouritesLoaded && favouriteIds.length === 0;
  const {
    view,
    restLine
  } = resolveBoardView({
    popular,
    rest,
    empty,
    myLeaguesEmpty: showMyLeaguesEmptyState
  });
  // A group is a skeleton only while its one hydration attempt is in flight; afterwards rows
  // render with what arrived (a missing team shows its id, as before).
  const isHydrating = (competitionId: string, group: FUSportsLivescoreCompetitionGroup) => !isGroupHydrated(group, sharedEntities) && !isSettled(competitionId);

  // Any broadcasters at all for this day? Drives whether rows reserve the TV slot;
  // when nothing's broadcast on this date, time columns sit flush with no empty gutter.
  const anyTvForDay = Object.keys(tvByMatch).length > 0;
  const toggleFavourite = useCallback((competitionId: string) => {
    const next = favouriteIds.includes(competitionId) ? favouriteIds.filter(id => id !== competitionId) : [...favouriteIds, competitionId];
    saveFavourites(next);
  }, [favouriteIds, saveFavourites]);
  return <div className={embedded ? "w-full space-y-3" : "w-full max-w-350 mx-auto px-4 py-4 space-y-3"}>
            {!embedded && <AdSlot id="content-top" />}

            <div className="flex items-center justify-between gap-4">
                {!embedded && <h1 className="text-2xl font-bold tracking-tight shrink-0">{ls.title}</h1>}
                {sports.length > 1 && <div className="hidden md:block shrink-0">
                        <LivescoreSportSelector sport={sport} onChange={setSport} sports={sports} />
                    </div>}
            </div>

            {sports.length > 1 && <div className="md:hidden overflow-x-auto">
                    <LivescoreSportSelector sport={sport} onChange={setSport} sports={sports} />
                </div>}

            <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0 overflow-x-auto">
                    <LivescoreFilters filter={filter} onChange={setFilter} allowedFilters={activeFilters} />
                </div>
                {activeFilters.includes("my-leagues") && <button onClick={() => setIsPickerOpen(true)} className="shrink-0 p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground cursor-pointer" aria-label="Manage my leagues" title="Manage my leagues">
                        <SlidersHorizontal className="w-4 h-4" />
                    </button>}
                {SPORTS_WITH_DATE_NAV.has(sport) && <div className="hidden md:block shrink-0">
                        <LivescoreDateNav date={date} onChange={setDate} />
                    </div>}
            </div>

            {SPORTS_WITH_DATE_NAV.has(sport) && <div className="md:hidden">
                    <LivescoreDateNav date={date} onChange={setDate} />
                </div>}

            {activeCompetitionId && (() => {
      const competition = asCompetition(sharedEntities, activeCompetitionId);
      return <button type="button" onClick={() => setActiveCompetitionId(null)} className="inline-flex items-center gap-1.5 px-3 py-1 text-xs rounded-full bg-muted text-foreground border border-border hover:bg-muted/70 transition-colors cursor-pointer" aria-label={ls.clearCompetitionFilter ?? "Clear competition filter"}>
                            <span aria-hidden>✕</span>
                            {competition?.assets?.logo &&
        // eslint-disable-next-line @next/next/no-img-element
        <img src={competition.assets.logo} alt="" className="w-4 h-4 rounded-full" />}
                            <span>{competition?.name ?? activeCompetitionId}</span>
                        </button>;
    })()}

            {view === "loading" ? <div className="text-center py-12 text-muted-foreground text-sm">{ls.loading}</div> : view === "my-leagues-empty" ? <div className="text-center py-12 text-muted-foreground text-sm space-y-3">
                    <p>{ls.picker.noFavourites}</p>
                    <button onClick={() => setIsPickerOpen(true)} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors cursor-pointer">
                        <SlidersHorizontal className="w-4 h-4" />
                        {ls.picker.addYourLeagues}
                    </button>
                </div> : view === "empty" ? <div className="text-center py-12 text-muted-foreground text-sm">{ls.noMatches}</div> : view === "unavailable" ? <div className="text-center py-12 text-muted-foreground text-sm">{ls.boardUnavailable}</div> : <div className="space-y-3">
                    {groups.map(group => <LivescoreMatchGroup key={group.competitionId} group={group} entities={sharedEntities} sport={sport} filter={filter} oddsMap={oddsMap} isMobile={isMobile} tvByMatch={tvByMatch} reserveTvSlot={anyTvForDay} hydrating={isHydrating(group.competitionId, group)} {...sport === "football" && {
        isFavourite: favouriteIds.includes(group.competitionId),
        onToggleFavourite: toggleFavourite
      }} />)}
                    {collapsed.map(group => <LivescoreMatchGroup key={group.competitionId} group={group} entities={sharedEntities} sport={sport} filter={filter} oddsMap={oddsMap} isMobile={isMobile} tvByMatch={tvByMatch} reserveTvSlot={anyTvForDay} collapsed={!expandedIds.has(group.competitionId)} onToggle={() => toggleExpanded(group.competitionId)} hydrating={expandedIds.has(group.competitionId) && isHydrating(group.competitionId, group)} {...sport === "football" && {
        isFavourite: favouriteIds.includes(group.competitionId),
        onToggleFavourite: toggleFavourite
      }} />)}
                    {restLine && <div className="text-center py-4 text-muted-foreground text-sm" aria-live="polite">
                            {restLine === "loading" ? ls.moreCompetitionsLoading : ls.boardUnavailable}
                        </div>}
                </div>}

            <LivescoreLeaguePicker isOpen={isPickerOpen} onClose={() => setIsPickerOpen(false)} locale={locale} initialFavouriteIds={favouriteIds} onSave={saveFavourites} />

            {!embedded && <AdSlot id="content-bottom" />}
        </div>;
}